import store from '../../../../state';
import LiveWeatherService from '../../../../services/LiveWeatherService';
import { WxStationBinaryService, WxStationMatch } from '../../../../services/WxStationBinaryService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

function haversineDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function getAqiCategoryAndColor(aqi: number): { category: string; color: string; bg: string; border: string } {
  if (aqi <= 50) {
    return { category: 'Good', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)' };
  } else if (aqi <= 100) {
    return { category: 'Moderate', color: '#eab308', bg: 'rgba(234, 179, 8, 0.1)', border: 'rgba(234, 179, 8, 0.3)' };
  } else if (aqi <= 150) {
    return { category: 'Unhealthy for Sensitive', color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.3)' };
  } else if (aqi <= 200) {
    return { category: 'Unhealthy', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)' };
  } else if (aqi <= 300) {
    return { category: 'Very Unhealthy', color: '#a855f7', bg: 'rgba(168, 85, 247, 0.1)', border: 'rgba(168, 85, 247, 0.3)' };
  } else {
    return { category: 'Hazardous', color: '#881337', bg: 'rgba(136, 19, 55, 0.15)', border: 'rgba(136, 19, 55, 0.4)' };
  }
}

function findNearestAqiStation(geoJson: any, lat: number, lon: number): { aqi: number; name: string; distanceMiles: number; param?: string } | null {
  if (!geoJson || !Array.isArray(geoJson.features) || geoJson.features.length === 0) return null;

  let bestStation: any = null;
  let minDistance = Infinity;

  for (const feature of geoJson.features) {
    const coords = feature.geometry?.coordinates;
    if (!coords || coords.length < 2) continue;
    const sLon = coords[0];
    const sLat = coords[1];
    const dist = haversineDistanceMiles(lat, lon, sLat, sLon);
    if (dist < minDistance) {
      const p = feature.properties || {};
      const aqiVal = typeof p.aqi === 'number' ? p.aqi : (typeof p.aqhi === 'number' ? Math.round(p.aqhi * 10) : null);
      if (aqiVal !== null && aqiVal !== undefined) {
        minDistance = dist;
        bestStation = {
          aqi: aqiVal,
          name: p.name || 'Station',
          distanceMiles: dist,
          param: p.param || 'PM2.5'
        };
      }
    }
  }

  return bestStation;
}

export class Glance2x2Widget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Current Conditions';
    const data = ctx.data;
    const isImperial = store.getState().units === 'imperial';
    const tempUnit = isImperial ? '°' : '°C';
    const speedUnit = isImperial ? 'mph' : 'km/h';

    const currentTemp = data.tempsForecast && data.tempsForecast.length ? data.tempsForecast[0] : 68;
    let dispTemp = isImperial ? Math.round(currentTemp) : Math.round((currentTemp - 32) * 5 / 9);

    const wind = data.windSpeed && data.windSpeed.length ? data.windSpeed[0] : 8;
    let dispWind = isImperial ? Math.round(wind) : Math.round(wind * 1.60934);
    let windDirPrefix = '';

    const cloud = data.cloudCover && data.cloudCover.length ? data.cloudCover[0] : 25;
    let icon = cloud > 75 ? '☁️' : (cloud > 35 ? '⛅' : '☀️');
    let condition = cloud > 75 ? 'Overcast' : (cloud > 35 ? 'Partly Cloudy' : 'Clear');

    const poiElevFt = ctx.elev ? Math.round(ctx.elev * 3.28084) : null;
    const cachedWxStn = WxStationBinaryService.getCachedBestStationForPoint(ctx.lat, ctx.lon, poiElevFt);
    let wxStnBadgeHtml = '';
    let wxMetaLineHtml = '';

    const formatWxStationMatch = (match: WxStationMatch) => {
      const tVal = isImperial ? Math.round(match.tempF) : Math.round((match.tempF - 32) * 5 / 9);
      const wSpd = match.wspdMph !== null
        ? (isImperial ? Math.round(match.wspdMph) : Math.round(match.wspdMph * 1.60934))
        : dispWind;
      const wDir = match.wdirCompass ? `${match.wdirCompass} ` : '';
      const gustStr = (match.wgstMph !== null && match.wspdMph !== null && match.wgstMph > match.wspdMph)
        ? ` (G${isImperial ? Math.round(match.wgstMph) : Math.round(match.wgstMph * 1.60934)})`
        : '';
      const elevStr = match.elevFt !== null
        ? (isImperial ? `${match.elevFt.toLocaleString()}′ · ` : `${Math.round(match.elevFt * 0.3048)}m · `)
        : '';
      const stnLabelText = `${match.id} · ${match.name} (${elevStr}${match.distanceMiles.toFixed(1)} mi)`;
      const metaParts: string[] = [];
      if (match.dewF !== null) {
        const dVal = isImperial ? Math.round(match.dewF) : Math.round((match.dewF - 32) * 5 / 9);
        metaParts.push(`Dew Pt ${dVal}${tempUnit}`);
      }
      if (match.rhPct !== null) {
        metaParts.push(`RH ${match.rhPct}%`);
      }
      return {
        tVal,
        wSpd,
        wDir,
        gustStr,
        stnLabelText,
        metaLine: metaParts.join(' · '),
        condText: match.conditionText,
        condIcon: match.conditionIcon
      };
    };

    if (cachedWxStn) {
      const fmt = formatWxStationMatch(cachedWxStn);
      dispTemp = fmt.tVal;
      dispWind = fmt.wSpd;
      windDirPrefix = fmt.wDir;
      icon = fmt.condIcon;
      condition = fmt.condText;
      wxStnBadgeHtml = fmt.stnLabelText;
      wxMetaLineHtml = fmt.metaLine;
    }

    // Lightning status calculation
    const obs = data.recentObserved;
    const strikes = obs?.lightningStrikes50mi ?? 0;
    const hasLightning = (obs?.hasLightning ?? false) || strikes > 0 || !!(data.lightningData && (data.lightningData.features?.length > 0 || (Array.isArray(data.lightningData) && data.lightningData.length > 0)));
    const lightningText = hasLightning ? 'Lightning Detected Within 50 Miles' : 'No Lightning';
    const lightningSub = hasLightning 
      ? (strikes > 0 ? `${strikes} strike${strikes === 1 ? '' : 's'} recorded nearby` : 'Active convective activity')
      : '50-mile scan clear';
    const lightningColor = hasLightning ? '#ef4444' : '#10b981';
    const lightningBg = hasLightning ? 'rgba(239, 68, 68, 0.12)' : 'rgba(16, 185, 129, 0.08)';
    const lightningBorder = hasLightning ? 'rgba(239, 68, 68, 0.35)' : 'rgba(16, 185, 129, 0.25)';

    // Nearest AQI Station calculation (cached or fallback to modeled)
    let aqiVal = (data.aqiValues && data.aqiValues.length > 0) ? Math.round(data.aqiValues[0]) : 30;
    let stnName = 'AirNow Station';
    let stnDistStr = '';

    const cachedAqiStationsGeoJson = LiveWeatherService.getInstance().getCachedAqiStationsGeoJson();
    if (cachedAqiStationsGeoJson) {
      const nearest = findNearestAqiStation(cachedAqiStationsGeoJson, ctx.lat, ctx.lon);
      if (nearest) {
        aqiVal = nearest.aqi;
        stnName = nearest.name;
        stnDistStr = `${nearest.distanceMiles.toFixed(1)} mi`;
      }
    }

    const aqiInfo = getAqiCategoryAndColor(aqiVal);
    const stnLabel = stnDistStr ? `${stnName} (${stnDistStr})` : stnName;

    return `
      <div class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; gap: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff; flex-shrink: 0;">🧭 ${title}</span>
          <span id="glance-wx-stn-${widget.id}" style="font-size: 0.68rem; color: #94a3b8; font-family: monospace; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-left: auto;" title="${wxStnBadgeHtml}">${wxStnBadgeHtml}</span>
        </div>
        
        <!-- Hero Temperature & Conditions -->
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 0 8px 0;">
          <div style="display: flex; align-items: baseline; gap: 4px;">
            <span id="glance-wx-temp-${widget.id}" style="font-size: 2.2rem; font-weight: 800; color: #f8fafc; font-family: monospace; line-height: 1;">${dispTemp}${tempUnit}</span>
            <span id="glance-wx-icon-${widget.id}" style="font-size: 1.5rem; margin-left: 6px;">${icon}</span>
          </div>
          <div style="text-align: right;">
            <div id="glance-wx-cond-${widget.id}" style="font-size: 0.8rem; font-weight: 700; color: #38bdf8;">${condition}</div>
            <div id="glance-wx-wind-${widget.id}" style="font-size: 0.72rem; color: #94a3b8; font-family: monospace;">Wind ${windDirPrefix}${dispWind} ${speedUnit}</div>
            <div id="glance-wx-meta-${widget.id}" style="font-size: 0.66rem; color: #64748b; font-family: monospace; margin-top: 1px; ${wxMetaLineHtml ? '' : 'display: none;'}">${wxMetaLineHtml}</div>
          </div>
        </div>

        <!-- 2x3 Glance Extensions: Lightning & Nearest AQI Station -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 4px;">
          <!-- Lightning Box -->
          <div style="background: ${lightningBg}; border: 1px solid ${lightningBorder}; border-radius: 10px; padding: 8px 10px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 2.2rem; font-weight: 800; line-height: 1; color: ${lightningColor}; flex-shrink: 0;">⚡</span>
            <div style="min-width: 0;">
              <div style="font-size: 0.76rem; font-weight: 800; color: ${lightningColor}; line-height: 1.2;">${lightningText}</div>
              <div style="font-size: 0.65rem; color: #94a3b8; margin-top: 2px;">${lightningSub}</div>
            </div>
          </div>

          <!-- Nearest AQI Station Box -->
          <div id="glance-aqi-box-${widget.id}" style="background: ${aqiInfo.bg}; border: 1px solid ${aqiInfo.border}; border-radius: 10px; padding: 8px 10px; display: flex; align-items: center; gap: 8px;">
            <span id="glance-aqi-val-${widget.id}" style="font-size: 2.2rem; font-weight: 800; line-height: 1; color: ${aqiInfo.color}; font-family: monospace; flex-shrink: 0;">${aqiVal}</span>
            <div style="min-width: 0;">
              <div id="glance-aqi-cat-${widget.id}" style="font-size: 0.76rem; font-weight: 800; color: ${aqiInfo.color}; line-height: 1.2;">AQI · ${aqiInfo.category}</div>
              <div id="glance-aqi-stn-${widget.id}" style="font-size: 0.65rem; color: #94a3b8; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${stnLabel}">${stnLabel}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const aqiBox = document.getElementById(`glance-aqi-box-${widget.id}`);
    const aqiValEl = document.getElementById(`glance-aqi-val-${widget.id}`);
    const aqiCatEl = document.getElementById(`glance-aqi-cat-${widget.id}`);
    const aqiStnEl = document.getElementById(`glance-aqi-stn-${widget.id}`);

    const applyNearestStation = (geoJson: any) => {
      const nearest = findNearestAqiStation(geoJson, ctx.lat, ctx.lon);
      if (nearest && aqiBox && aqiValEl && aqiCatEl && aqiStnEl) {
        const info = getAqiCategoryAndColor(nearest.aqi);
        aqiBox.style.background = info.bg;
        aqiBox.style.borderColor = info.border;
        aqiValEl.textContent = String(nearest.aqi);
        aqiValEl.style.color = info.color;
        aqiCatEl.textContent = `AQI · ${info.category}`;
        aqiCatEl.style.color = info.color;
        const stnLabel = `${nearest.name} (${nearest.distanceMiles.toFixed(1)} mi)`;
        aqiStnEl.textContent = stnLabel;
        aqiStnEl.title = stnLabel;
      }
    };

    const liveService = LiveWeatherService.getInstance();
    const cached = liveService.getCachedAqiStationsGeoJson();
    if (cached) {
      applyNearestStation(cached);
    }

    liveService
      .fetchAqiStationsGeoJson()
      .then(geoJson => {
        if (geoJson) {
          applyNearestStation(geoJson);
        }
      })
      .catch(err => {
        console.warn('[Glance2x2Widget] Failed to load AQI stations:', err);
      });

    // Nearest AWOS / ASOS / RAWS Station via Zoom-4 Binary Shard
    const wxStnEl = document.getElementById(`glance-wx-stn-${widget.id}`);
    const wxTempEl = document.getElementById(`glance-wx-temp-${widget.id}`);
    const wxIconEl = document.getElementById(`glance-wx-icon-${widget.id}`);
    const wxCondEl = document.getElementById(`glance-wx-cond-${widget.id}`);
    const wxWindEl = document.getElementById(`glance-wx-wind-${widget.id}`);
    const wxMetaEl = document.getElementById(`glance-wx-meta-${widget.id}`);

    const state = store.getState();
    const isImperial = state.units === 'imperial';
    const tempUnit = isImperial ? '°' : '°C';
    const speedUnit = isImperial ? 'mph' : 'km/h';
    const poiElevFt = ctx.elev ? Math.round(ctx.elev * 3.28084) : null;

    WxStationBinaryService.getBestStationForPoint(ctx.lat, ctx.lon, poiElevFt, state.manifest || undefined)
      .then(match => {
        if (!match) return;
        const tVal = isImperial ? Math.round(match.tempF) : Math.round((match.tempF - 32) * 5 / 9);
        if (wxTempEl) wxTempEl.textContent = `${tVal}${tempUnit}`;
        if (wxIconEl) wxIconEl.textContent = match.conditionIcon;
        if (wxCondEl) wxCondEl.textContent = match.conditionText;

        if (wxWindEl && match.wspdMph !== null) {
          const wSpd = isImperial ? Math.round(match.wspdMph) : Math.round(match.wspdMph * 1.60934);
          const wDir = match.wdirCompass ? `${match.wdirCompass} ` : '';
          const gustStr = (match.wgstMph !== null && match.wgstMph > match.wspdMph)
            ? ` (G${isImperial ? Math.round(match.wgstMph) : Math.round(match.wgstMph * 1.60934)})`
            : '';
          wxWindEl.textContent = `Wind ${wDir}${wSpd} ${speedUnit}${gustStr}`;
        }

        if (wxStnEl) {
          const elevStr = match.elevFt !== null
            ? (isImperial ? `${match.elevFt.toLocaleString()}′ · ` : `${Math.round(match.elevFt * 0.3048)}m · `)
            : '';
          const label = `${match.id} · ${match.name} (${elevStr}${match.distanceMiles.toFixed(1)} mi)`;
          wxStnEl.textContent = label;
          wxStnEl.title = label;
        }

        if (wxMetaEl) {
          const metaParts: string[] = [];
          if (match.dewF !== null) {
            const dVal = isImperial ? Math.round(match.dewF) : Math.round((match.dewF - 32) * 5 / 9);
            metaParts.push(`Dew Pt ${dVal}${tempUnit}`);
          }
          if (match.rhPct !== null) {
            metaParts.push(`RH ${match.rhPct}%`);
          }
          if (metaParts.length > 0) {
            wxMetaEl.textContent = metaParts.join(' · ');
            wxMetaEl.style.display = '';
          }
        }
      })
      .catch(err => {
        console.warn('[Glance2x2Widget] Failed to load nearest WX station shard:', err);
      });
  }
}
